import React from "react";
import { Link } from "react-router-dom";
import "./styles/IntelligentSmartCitiesSolutions.css";

const focusAreas = [
  {
    title: "Smarter resource usage",
    text: "Use IoT sensors and connected infrastructure to better understand how energy, water, and other resources are used across the city.",
  },
  {
    title: "Better citizen services",
    text: "Improve day to day experiences with more responsive lighting, transport, public safety, and city services.",
  },
  {
    title: "Data informed decisions",
    text: "Turn sensor data and analytics into practical insight that supports long term planning and real time response.",
  },
];

const cityPillars = [
  {
    title: "Connected infrastructure",
    text: "Streetlights, traffic signals, utilities, and public spaces equipped with IoT sensors for real time monitoring.",
  },
  {
    title: "Urban analytics",
    text: "Data platforms that aggregate and analyze information from sensors, devices, and city systems.",
  },
  {
    title: "Citizen centric services",
    text: "Digital services that help residents access information, move around more easily, and interact with the city.",
  },
  {
    title: "Sustainability and resiliency",
    text: "Initiatives that use IoT and analytics to support smarter energy, water, and environmental management.",
  },
];

const solutionBlocks = [
  {
    label: "Mobility & traffic",
    text: "Smart signals, parking, and transport telemetry that help manage congestion and improve travel flow.",
  },
  {
    label: "Energy & lighting",
    text: "Adaptive lighting and smart energy metering to support more efficient power usage and cost control.",
  },
  {
    label: "Environment & utilities",
    text: "Sensors that monitor air quality, water usage, and key utility performance to support better resource planning.",
  },
  {
    label: "Safety & public spaces",
    text: "Connected infrastructure that helps city teams react more quickly to issues affecting public areas.",
  },
];

const outcomeCards = [
  {
    title: "More efficient operations",
    text: "Use real time data to reduce waste, improve responsiveness, and support smarter city management.",
  },
  {
    title: "Improved quality of life",
    text: "Enhance citizen experiences with better transport, services, and safer, more responsive public spaces.",
  },
  {
    title: "A foundation for innovation",
    text: "Create an IoT ready infrastructure that supports new applications and long term economic growth.",
  },
];

const fitCards = [
  {
    title: "Municipal and regional projects",
    text: "For city and regional teams planning smart infrastructure, utilities, or urban services programs.",
  },
  {
    title: "Campus and district initiatives",
    text: "For campuses, districts, or zones building connected spaces with integrated sensing and analytics.",
  },
  {
    title: "Innovation led developments",
    text: "For development projects that want an IoT ready framework from the beginning.",
  },
];

export default function IntelligentSmartCitiesSolutions() {
  return (
    <div className="iotPage">
      <section className="iotHero">
        <div className="iotContainer iotHero__container">
          <div className="iotHero__content">
            <p className="iotEyebrow">IoT Intelligent Smart Cities Solutions</p>

            <h1 className="iotHero__title">
              Build more connected, data‑aware, and citizen‑focused cities.
            </h1>

            <p className="iotHero__text">
              Our IoT Intelligent Smart Cities Solutions help integrate sensors,
              data analytics, and smart infrastructure so cities can optimize
              resource usage, enhance citizen services, and create more
              sustainable, interconnected urban environments.
            </p>

            <div className="iotHero__actions">
              <Link to="/contact" className="iotBtn iotBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="iotBtn iotBtn--ghost">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="iotHero__visual">
            <div className="iotCityPanel">
              <div className="iotCityPanel__header">
                <span className="iotChip">IoT enabled city</span>
                <span className="iotChip iotChip--soft">Sensors + data</span>
              </div>

              <div className="iotCitySkyline">
                <div className="iotBuilding iotBuilding--one">
                  <span className="iotDot iotDot--energy">Energy</span>
                </div>
                <div className="iotBuilding iotBuilding--two">
                  <span className="iotDot iotDot--traffic">Traffic</span>
                </div>
                <div className="iotBuilding iotBuilding--three">
                  <span className="iotDot iotDot--env">Environment</span>
                </div>
                <div className="iotRoad">
                  <span className="iotCar" />
                  <span className="iotSignal">Signal</span>
                </div>
                <div className="iotPark">
                  <span className="iotDot iotDot--public">Public space</span>
                </div>
              </div>

              <div className="iotCityPanel__footer">
                <div className="iotMetric">
                  <strong>Urban data</strong>
                  <span>Collected from sensors across infrastructure and services</span>
                </div>
                <div className="iotMetric">
                  <strong>City outcomes</strong>
                  <span>Used to support efficiency, sustainability, and new services</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="iotFocusSection">
        <div className="iotContainer">
          <div className="iotFocusGrid">
            {focusAreas.map((item) => (
              <article key={item.title} className="iotFocusCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="iotPillarsSection">
        <div className="iotContainer iotPillarsWrap">
          <div className="iotSectionHead">
            <p className="iotEyebrow">What the solution includes</p>
            <h2 className="iotTitle">
              A smart city approach that connects infrastructure, data, and services
            </h2>
            <p className="iotText">
              IoT is most effective for cities when sensing, analytics, and
              service delivery work together rather than as isolated projects.
            </p>
          </div>

          <div className="iotPillarsGrid">
            {cityPillars.map((item) => (
              <article key={item.title} className="iotPillarCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="iotSolutionsSection">
        <div className="iotContainer">
          <div className="iotSectionHead iotSectionHead--center">
            <p className="iotEyebrow">Urban solution areas</p>
            <h2 className="iotTitle">
              Practical IoT applications that support city operations
            </h2>
            <p className="iotText">
              Smart city initiatives often begin in areas where real time data
              can quickly improve service quality, experience, or resource usage.
            </p>
          </div>

          <div className="iotSolutionsGrid">
            {solutionBlocks.map((item) => (
              <article key={item.label} className="iotSolutionCard">
                <strong>{item.label}</strong>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="iotOutcomesSection">
        <div className="iotContainer">
          <div className="iotSectionHead">
            <p className="iotEyebrow">City level outcomes</p>
            <h2 className="iotTitle">
              What IoT enabled smart city initiatives help improve
            </h2>
            <p className="iotText">
              The goal is not just to deploy sensors, but to support better
              outcomes for operations, citizens, and long term city planning.
            </p>
          </div>

          <div className="iotOutcomeGrid">
            {outcomeCards.map((item) => (
              <article key={item.title} className="iotOutcomeCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="iotFitSection">
        <div className="iotContainer">
          <div className="iotSectionHead iotSectionHead--center">
            <p className="iotEyebrow">Best fit</p>
            <h2 className="iotTitle">
              Where IoT Intelligent Smart Cities Solutions are most useful
            </h2>
            <p className="iotText">
              These services are designed for city scale and district scale
              initiatives where connectivity, sensing, and data are central to
              the strategy.
            </p>
          </div>

          <div className="iotFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="iotFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="iotCta">
        <div className="iotContainer iotCta__container">
          <div>
            <p className="iotEyebrow iotEyebrow--light">Next step</p>
            <h2 className="iotCta__title">
              Plan an IoT smart city roadmap that matches your goals
            </h2>
            <p className="iotCta__text">
              If you are exploring IoT for smart city projects, infrastructure
              monitoring, or citizen focused services, ComNetworks.net can help
              design a practical approach that connects devices, data, and
              outcomes.
            </p>
          </div>

          <div className="iotCta__actions">
            <Link to="/contact" className="iotBtn iotBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="iotBtn iotBtn--ghostLight">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
